import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { StagedUser } from '../services/HxcStagingService';
import { parseCSV, ParsedCSVRow } from '../utils/csvParser';

interface CSVUploadDialogProps {
  open: boolean;
  onClose: () => void;
  existingUsers: StagedUser[];
  onCreateUser: (userData: Omit<StagedUser, 'id'>) => Promise<StagedUser>;
  onRefresh: () => Promise<void>;
}

type DialogState = 'initial' | 'processing' | 'results';

interface PostResult {
  success: boolean;
  row: ParsedCSVRow;
  error?: string;
}

interface FinalResults {
  parseErrors: number;
  duplicates: number;
  created: number;
  failed: number;
  parseErrorDetails: Array<{ row: number; message: string }>;
  duplicateEmails: string[];
  failedDetails: Array<{ rowNumber: number; email: string; error: string }>;
}

const CSVUploadDialog: React.FC<CSVUploadDialogProps> = ({
  open,
  onClose,
  existingUsers,
  onCreateUser,
  onRefresh
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>('initial');
  const [finalResults, setFinalResults] = useState<FinalResults | null>(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Reset dialog state when closing
  const handleClose = useCallback(async () => {
    // Refresh table if users were created
    if (shouldRefresh) {
      await onRefresh();
    }
    
    // Clear all state
    setSelectedFile(null);
    setDialogState('initial');
    setFinalResults(null);
    setUploadProgress({ current: 0, total: 0 });
    setShouldRefresh(false);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    onClose();
  }, [onClose, onRefresh, shouldRefresh]);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setErrorModalMessage('Please select a valid .csv file');
      setErrorModalOpen(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrorModalMessage('File size must be less than 5MB. Please select a smaller file.');
      setErrorModalOpen(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
  };

  // Handle cancel
  const handleCancel = () => {
    if (dialogState === 'processing') {
      // Don't allow closing during processing
      return;
    }
    handleClose();
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    setDialogState('processing');

    try {
      // Step 1: Parse the CSV file
      const parseResults = await parseCSV(selectedFile, existingUsers);
      
      // Step 2: Send POST requests for each valid row
      const postResults: PostResult[] = [];
      const totalToUpload = parseResults.validRows.length;
      
      setUploadProgress({ current: 0, total: totalToUpload });

      for (let i = 0; i < parseResults.validRows.length; i++) {
        const row = parseResults.validRows[i];
        
        try {
          await onCreateUser({
            accountName: row.accountName,
            sfdcAccountId: row.sfdcAccountId,
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email
          });
          
          postResults.push({
            success: true,
            row
          });
        } catch (error: any) {
          // Extract error message from API response if available
          const errorMessage = error?.response?.data?.message || 
                               (error instanceof Error ? error.message : 'Unknown error');
          
          postResults.push({
            success: false,
            row,
            error: errorMessage
          });
        }
        
        setUploadProgress({ current: i + 1, total: totalToUpload });
      }

      // Step 3: Compile final results
      const successfulPosts = postResults.filter(r => r.success).length;
      const failedPosts = postResults.filter(r => !r.success);

      setFinalResults({
        parseErrors: parseResults.errorCount,
        duplicates: parseResults.duplicateCount,
        created: successfulPosts,
        failed: failedPosts.length,
        parseErrorDetails: parseResults.errors,
        duplicateEmails: parseResults.duplicates,
        failedDetails: failedPosts.map(f => ({
          rowNumber: f.row.rowNumber,
          email: f.row.email,
          error: f.error || 'Unknown error'
        }))
      });

      // Step 4: Mark that table should be refreshed on close if any users were created
      if (successfulPosts > 0) {
        setShouldRefresh(true);
      }

      setDialogState('results');
    } catch (error) {
      // Handle unexpected errors
      setFinalResults({
        parseErrors: 1,
        duplicates: 0,
        created: 0,
        failed: 0,
        parseErrorDetails: [{
          row: 0,
          message: error instanceof Error ? error.message : 'Unknown error occurred while processing CSV'
        }],
        duplicateEmails: [],
        failedDetails: []
      });
      setDialogState('results');
    } finally {
      // Clear file reference after processing
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Render dialog content based on state
  const renderContent = () => {
    switch (dialogState) {
      case 'initial':
        return (
          <>
            {/* File Requirements */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Required CSV Columns:
              </Typography>
              <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="• Account ID" 
                    secondary="15 character alphanumeric code"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="• Account Name" 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="• First Name" 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="• Last Name" 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="• Email" 
                    secondary="Valid email address"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Additional columns are allowed but will be ignored. Column headers are case-insensitive.
              </Typography>
            </Paper>

            {/* File Selector */}
            <Box
              sx={{
                border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="csv-file-input"
              />
              <label htmlFor="csv-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Select CSV File
                </Button>
              </label>
              
              {selectedFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Selected file:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Maximum file size: 5MB
            </Typography>
          </>
        );

      case 'processing':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6
            }}
          >
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" sx={{ mt: 3, fontWeight: 600 }}>
              {uploadProgress.total === 0 ? 'Parsing CSV...' : 'Creating Users...'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {uploadProgress.total === 0 
                ? 'Please wait while we parse and validate your file'
                : `Processing ${uploadProgress.current} of ${uploadProgress.total} users`
              }
            </Typography>
            {uploadProgress.total > 0 && (
              <Box sx={{ width: '80%', mt: 3 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(uploadProgress.current / uploadProgress.total) * 100} 
                />
              </Box>
            )}
          </Box>
        );

      case 'results':
        if (!finalResults) return null;

        const hasErrors = finalResults.parseErrors > 0 || finalResults.failed > 0;
        const hasDuplicates = finalResults.duplicates > 0;
        const shouldAutoClose = !hasErrors && !hasDuplicates;

        return (
          <Box>
            {/* Summary */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: hasErrors 
                  ? alpha(theme.palette.error.main, 0.05)
                  : alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${hasErrors 
                  ? alpha(theme.palette.error.main, 0.2)
                  : alpha(theme.palette.success.main, 0.2)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Upload Complete
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2">
                    <strong>{finalResults.created}</strong> Created
                  </Typography>
                </Box>
                
                {hasDuplicates && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" fontSize="small" />
                    <Typography variant="body2">
                      <strong>{finalResults.duplicates}</strong> Duplicate{finalResults.duplicates !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                )}
                
                {finalResults.parseErrors > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon color="error" fontSize="small" />
                    <Typography variant="body2">
                      <strong>{finalResults.parseErrors}</strong> Parse Error{finalResults.parseErrors !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                )}
                
                {finalResults.failed > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon color="error" fontSize="small" />
                    <Typography variant="body2">
                      <strong>{finalResults.failed}</strong> Failed to Create
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Duplicates */}
            {hasDuplicates && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  The following emails already exist in the staging list and were skipped:
                </Alert>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    borderRadius: 2,
                    maxHeight: 200,
                    overflow: 'auto'
                  }}
                >
                  <List dense>
                    {finalResults.duplicateEmails.map((email, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary={`• ${email}`}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            fontFamily: 'monospace'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {/* Parse Errors */}
            {finalResults.parseErrors > 0 && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="error" sx={{ mb: 1 }}>
                  The following rows had validation errors:
                </Alert>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.error.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    borderRadius: 2,
                    maxHeight: 300,
                    overflow: 'auto'
                  }}
                >
                  <List dense>
                    {finalResults.parseErrorDetails.map((error, index) => (
                      <ListItem key={index} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemText 
                          primary={error.row > 0 ? `Row ${error.row}` : 'File Error'}
                          secondary={error.message}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            fontWeight: 600,
                            color: 'error.main'
                          }}
                          secondaryTypographyProps={{ 
                            variant: 'body2',
                            color: 'text.primary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {/* API Errors */}
            {finalResults.failed > 0 && (
              <Box>
                <Alert severity="error" sx={{ mb: 1 }}>
                  The following users failed to be created (API errors):
                </Alert>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.error.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    borderRadius: 2,
                    maxHeight: 300,
                    overflow: 'auto'
                  }}
                >
                  <List dense>
                    {finalResults.failedDetails.map((failure, index) => (
                      <ListItem key={index} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemText 
                          primary={`Row ${failure.rowNumber} ${failure.email}`}
                          secondary={failure.error}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            color: 'error.main'
                          }}
                          secondaryTypographyProps={{ 
                            variant: 'body2',
                            color: 'text.primary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {shouldAutoClose && (
              <Alert severity="success" sx={{ mt: 2 }}>
                All valid rows were successfully created! The staging table will be refreshed when you close this dialog.
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // Render action buttons based on state
  const renderActions = () => {
    switch (dialogState) {
      case 'initial':
        return (
          <>
            <Button
              onClick={handleCancel}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!selectedFile}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                }
              }}
            >
              Upload
            </Button>
          </>
        );

      case 'processing':
        return null; // No buttons during processing

      case 'results':
        return (
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
              }
            }}
          >
            Close
          </Button>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={dialogState === 'processing' ? undefined : handleCancel}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.5rem',
            pb: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
          }}
        >
          Upload CSV File
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {renderContent()}
        </DialogContent>
        
        {(dialogState === 'initial' || dialogState === 'results') && (
          <DialogActions
            sx={{
              px: 3,
              pb: 2.5,
              pt: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
            }}
          >
            {renderActions()}
          </DialogActions>
        )}
      </Dialog>
      
      {/* Error Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: theme.palette.error.main,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <ErrorIcon />
          Invalid File
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1">
            {errorModalMessage}
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setErrorModalOpen(false)}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CSVUploadDialog;
